/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { UITarget } from '@/targets/ui-target';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import PaymentBadges from './payment-badges';
import { FOOTER_NAV_COLUMNS, type FooterNavColumn } from './footer-nav-config';

interface NavColumnsProps {
    paymentBadgesPage?: Promise<PageWithComponentData | null>;
}

function ColumnBody({
    column,
    paymentBadgesPage,
}: {
    column: FooterNavColumn;
    paymentBadgesPage?: Promise<PageWithComponentData | null>;
}): ReactElement {
    const { t } = useTranslation('footer');

    if (column.isContactColumn) {
        return (
            <div className="flex flex-col gap-4">
                <a
                    href="tel:+18007461539"
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {t('links.supportPhone')}
                </a>
                <PaymentBadges page={paymentBadgesPage} />
            </div>
        );
    }

    return (
        <ul className="flex flex-col gap-2">
            {column.links?.map((link) => (
                <li key={link.labelKey}>
                    <Link to={link.to} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                        {t(link.labelKey)}
                    </Link>
                </li>
            ))}
            {column.id === 'about' && <UITarget targetId="sfcc.footer.ourcompany.start" />}
        </ul>
    );
}

/**
 * Renders FOOTER_NAV_COLUMNS as a 4-column grid on desktop and a collapsible
 * accordion on mobile — same config array drives both layouts.
 */
export default function NavColumns({ paymentBadgesPage }: NavColumnsProps): ReactElement {
    const { t } = useTranslation('footer');

    return (
        <div>
            <div className="hidden lg:grid grid-cols-4 gap-8">
                {FOOTER_NAV_COLUMNS.map((column) => (
                    <div key={column.id}>
                        <h3 className="text-sm font-semibold text-foreground mb-4">{t(column.labelKey)}</h3>
                        <ColumnBody column={column} paymentBadgesPage={paymentBadgesPage} />
                    </div>
                ))}
            </div>

            <Accordion type="multiple" className="w-full lg:hidden">
                {FOOTER_NAV_COLUMNS.map((column) => (
                    <AccordionItem key={column.id} value={column.id}>
                        <AccordionTrigger className="text-sm font-semibold text-foreground">
                            {t(column.labelKey)}
                        </AccordionTrigger>
                        <AccordionContent>
                            <ColumnBody column={column} paymentBadgesPage={paymentBadgesPage} />
                        </AccordionContent>
                    </AccordionItem>
                ))}
            </Accordion>
        </div>
    );
}
